// pages/OpenLeaks/components/TimelineLeakCard/TimelineLeakCard.tsx
import React, { useState, useEffect, useContext } from 'react';
import { DataLeak, statusLabels, statusColors, severityColors } from "features/openleak";
import { UserContext } from "features/account";
import { checkClientBreachLink, linkBreachToClient } from "features/openleak";
import './TimelineLeakCard.scss';

interface TimelineLeakCardProps {
  leak: DataLeak;
  side: 'left' | 'right';
  onLinked?: () => void;
}

const TimelineLeakCard: React.FC<TimelineLeakCardProps> = ({ leak, side, onLinked }) => {
  const { user } = useContext(UserContext);

  const [isLinked, setIsLinked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vérifier si l'utilisateur a déjà lié cette fuite
  useEffect(() => {
    if (user?.id && leak.id) {
      checkClientBreachLink(leak.id, user?.type)
        .then((response) => {
          setIsLinked(response.isLinked);
        })
        .catch((err) => {
          console.error('Erreur vérification lien:', err);
        });
    }
  }, [user, leak.id]);

  // Format date: 15 Mars 2024
  const formatFullDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format affected count
  const formatAffectedCount = (count: number | null) => {
    if (!count) return 'Non spécifié';
    if (count >= 1_000_000) {
      return `${(count / 1_000_000).toFixed(1)}M personnes`;
    }
    if (count >= 1_000) {
      return `${(count / 1_000).toFixed(0)}k personnes`;
    }
    return `${count.toLocaleString()} personnes`;
  };

  // Get severity label
  const getSeverityLabel = (severity: string | null) => {
    if (!severity) return 'Non évalué';
    const labels = {
      critical: 'Critique',
      high: 'Élevé',
      medium: 'Moyen',
      low: 'Modéré'
    };
    return labels[severity as keyof typeof labels] || 'Non évalué';
  };

  // Handle "Je suis concerné" click
  const handleConcernedClick = () => {
    if (!user?.id) {
      // Rediriger vers la page de connexion ou afficher un modal
      window.location.href = '/particuliers/login?redirect=/open-leaks';
      return;
    }
    setShowConfirm(true);
  };

  // Confirm link
  const handleConfirmLink = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await linkBreachToClient(leak.id, '', user?.type);
      setIsLinked(true);
      setShowConfirm(false);
      if (onLinked) onLinked();

      // Optionnel: Afficher une notification de succès
      console.log('Fuite liée avec succès:', response);
    } catch (err: any) {
      if (err.response?.data?.alreadyLinked) {
        setIsLinked(true);
        setShowConfirm(false);
      } else {
        setError(err.response?.data?.error || 'Une erreur est survenue');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel
  const handleCancel = () => {
    setShowConfirm(false);
    setError(null);
  };

  // Split data types for display
  const dataTypesList = leak.DataClasses.map(type => type.trim());

  return (
    <div className={`timelineOpenLeak-card timelineOpenLeak-card--${side}`}>
      <div className="timelineOpenLeak-card__content">
        {/* Header with company and date badge */}
        <div className="timelineOpenLeak-card__header">
          <div className="timelineOpenLeak-card__company-info">
            <h3 className="timelineOpenLeak-card__company">{leak.Title || leak.Name}</h3>
            <div className="timelineOpenLeak-card__date-badge">
              {formatFullDate(leak.BreachDate)}
            </div>
          </div>
          <div className="timelineOpenLeak-card__badges">
            <div
              className="timelineOpenLeak-card__status"
              style={{ backgroundColor: statusColors[leak.BreachStatus] }}
            >
              {statusLabels[leak.BreachStatus]}
            </div>
            {leak.PwnCount && (
              <div className="timelineOpenLeak-card__affected">
                {formatAffectedCount(leak.PwnCount)}
              </div>
            )}
          </div>
        </div>

        {/* Data types */}
        {dataTypesList.length > 0 && dataTypesList[0] !== 'Non spécifié' && (
          <div className="timelineOpenLeak-card__data-types">
            <span className="data-types-label">Données exposées :</span>
            <div className="data-types-list">
              {dataTypesList.map((type: string, idx: number) => (
                <span key={idx} className="data-type-tag">
                  {type}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* "Je suis concerné" button */}
        <div className="timelineOpenLeak-card__action">
          {!isLinked ? (
            <button
              className="concerned-btn pointer"
              onClick={() => {handleConcernedClick()}}
              disabled={isLoading}
            >
              {isLoading ? 'Chargement...' : 'Je suis concerné ?'}
            </button>
          ) : (
            <div className="concerned-confirmed">
              <span className="check-icon">✓</span>
              <span>Vous avez signalé être concerné par cette fuite</span>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="timelineOpenLeak-card__error">
            {error}
          </div>
        )}

        {/* Confirmation modal inline */}
        {showConfirm && (
          <div className="timelineOpenLeak-card__confirm">
            <p>Confirmez-vous que vous êtes concerné par cette fuite de données ?</p>
            <div className="confirm-actions">
              <button
                className="confirm-btn confirm-btn--yes pointer"
                onClick={() => {handleConfirmLink()}}
                disabled={isLoading}
              >
                {isLoading ? 'Traitement...' : 'Oui, je confirme'}
              </button>
              <button
                className="confirm-btn confirm-btn--no"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineLeakCard;